import React, { useState, useEffect } from "react";
import { ProfileFormData } from "../../types/form";
import { AVAILABLE_TEMPLATES } from "../FormSteps/TemplateSelection";
import { GradientButton } from "./GradientButton";
import { HeadshotFAB } from "./HeadshotFAB";
import {TemplateProcessor} from "quickplate-template";
import { generateVCard } from "../../utils/formatters";

interface TemplatePreviewModalProps {
  isOpen: boolean;
  onClose: () => void;
  formData?: ProfileFormData;
  handleCreateProfile: () => void;
  isAuthenticated: boolean;
  isSubmitting: boolean;
}

export const TemplatePreviewModal: React.FC<TemplatePreviewModalProps> = ({
  isOpen,
  onClose,
  formData,
  handleCreateProfile,
  isAuthenticated,
  isSubmitting,
}) => {
  const [templateContent, setTemplateContent] = useState<string>("");
  const [isLoading, setIsLoading] = useState<boolean>(false);
  const [error, setError] = useState<string>("");
  const [templateName, setTemplateName] = useState<string>("");

  useEffect(() => {
    
    const loadTemplateContent = async () => {
      if (!formData?.selectedTemplate) {
        return;
      }
      
      setIsLoading(true);
      setError("");
      
      try {
        // Find the selected template
        const selectedTemplate = AVAILABLE_TEMPLATES.find(tmp => tmp.name === formData.selectedTemplate);
        
        
        setTemplateName(selectedTemplate?.name || "");
        
        fetch("template/"+selectedTemplate?.templatePath || "").then(res => {
          res.text().then(text => {
            setTemplateContent(text);
          });
        });

      } catch (error) {
        console.error("❌ Error loading template:", error);
        setError(error instanceof Error ? error.message : "Failed to load template");
      } finally {
        setIsLoading(false);
      }
    };

    loadTemplateContent();
  }, [formData, isOpen]); 

  
  
  if (!isOpen) {
    return null;
  }

  const templateProcessor = new TemplateProcessor();
  const previewData = formData ? {
    ...formData,
    businessLogoUrl: formData.businessLogo instanceof File ? URL.createObjectURL(formData.businessLogo) : formData.businessLogo || "",
    headshotUrl: formData.headshot instanceof File ? URL.createObjectURL(formData.headshot) : formData.headshot || "",
    faviconUrl: formData.faviconLogo instanceof File ? URL.createObjectURL(formData.faviconLogo) : formData.faviconLogo || "",
    fabButtonUrl: formData.fabButtonLogo instanceof File ? URL.createObjectURL(formData.fabButtonLogo) : formData.fabButtonLogo || "",
    streetAddress1: formData.address?.streetAddress1 || "",
    city: formData.address?.city || "",
    state: formData.address?.state || "",
    zipCode: formData.address?.zipCode || "",
    ...(formData.socialMedia
      ? Object.fromEntries(
          Object.entries(formData.socialMedia).map(
            ([key, value]) => [`${key}Link`, value]
          )
        )
      : {}),
    textLink: formData.phone ? `sms:${formData.phone}` : "",
    callLink: formData.phone ? `tel:${formData.phone}` : "",
    emailLink: formData.email ? `mailto:${formData.email}` : "",
    addContact: generateVCard(formData as ProfileFormData),
      ...(formData.reviews ? {reviews: formData.reviews.map(review => ({
        ...review,
        imageUrl: review.image instanceof File ? URL.createObjectURL(review.image) : review.image || ""
      }))} : {}),
  } : {};
  
  const processedTemplate = templateProcessor.process(templateContent, previewData);

  const renderedContent = processedTemplate;

  const faviconUrl = 
    formData?.faviconLogo instanceof File ? URL.createObjectURL(formData?.faviconLogo) : formData?.faviconLogo || "";
  const headshotUrl =
    formData?.headshot instanceof File ? URL.createObjectURL(formData.headshot) : formData?.headshot ||
    "";
  const fabButtonUrl =
    formData?.fabButtonLogo instanceof File ? URL.createObjectURL(formData?.fabButtonLogo) : formData?.fabButtonLogo || "";

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
      <div className="bg-white rounded-lg max-h-[95vh] overflow-hidden w-[500px] relative">
        {/* Browser Header */}
        <div className="bg-gray-100 border-b border-gray-300 px-4 py-2 flex items-center justify-between">
          <div className="flex items-center space-x-3">
            {/* Browser Controls */}
            <div className="flex space-x-2">
              <div className="w-3 h-3 bg-red-500 rounded-full" onClick={onClose}></div>
              <div className="w-3 h-3 bg-yellow-500 rounded-full"></div>
              <div className="w-3 h-3 bg-green-500 rounded-full"></div>
            </div>
            
            {/* Address Bar */}
            <div className="flex-1 mx-4 bg-white border border-gray-300 rounded-md px-3 py-1 flex items-center">
              <div className="w-4 h-4 mr-2">
                <img src={String(faviconUrl || headshotUrl)} alt="favicon" className="w-full h-full" />
              </div>
              <span className="text-sm text-gray-600 truncate">
                {formData?.firstName && formData?.lastName 
                  ? `${formData.firstName} ${formData.lastName} - Digital Business Card`
                  : "Digital Business Card Preview"
                }
              </span>
            </div>
          </div>
          
          {/* Close Button */}
          <button
            onClick={onClose}
            className="text-gray-400 hover:text-gray-600 text-xl font-bold ml-2"
          >
            ×
          </button>
        </div>

        {/* Template Content */}
        <div className="overflow-y-auto max-h-[calc(95vh-120px)]">
          {isLoading ? (
            <div className="flex items-center justify-center h-64">
              <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600"></div>
              <span className="ml-3 text-gray-600">Loading template...</span>
            </div>
          ) : error ? (
            <div className="text-center py-8">
              <div className="text-red-600 mb-4">
                <i className="fas fa-exclamation-triangle text-4xl"></i>
              </div>
              <h3 className="text-lg font-semibold text-gray-900 mb-2">
                Error Loading Template
              </h3>
              <p className="text-gray-600">{error}</p>
            </div>
          ) : (
            <div className="relative">
              <iframe
                srcDoc={renderedContent}
                className="w-full h-[600px] border-0"
                title={`${templateName} Template Preview`}
                sandbox="allow-scripts allow-same-origin"
              />
             
                <div className="absolute bottom-8 right-6 z-50">
                  <HeadshotFAB
                    url={String(fabButtonUrl || headshotUrl)}
                  />
                </div>
            
            </div>
          )}
        </div>

        {/* Action Buttons */}
        <div className="flex justify-between gap-3 p-6 border-t bg-gray-50">
          {/* Update Card Button */}
          <GradientButton
            variant="secondary"
            onClick={onClose}
            className="px-4 py-2 text-gray-600 hover:text-gray-800 border border-gray-300 rounded-md hover:bg-gray-50"
          >
            Close
          </GradientButton>

          {/* Create My Digital Business Card! Button */}
          <GradientButton
            variant="submit"
            onClick={handleCreateProfile}
            className="px-4 py-2 text-gray-600 hover:text-gray-800 border border-gray-300 rounded-md hover:bg-gray-50"
          >
            {isSubmitting
              ? "Creating Profile..."
              : isAuthenticated
                ? `Update Now`
                : `Create Now`}
          </GradientButton>
        </div>
      </div>
    </div>
  );
};
